/**
 * Per-unit document cell used by the Revenue table.
 *
 *  - When no file: shows a paperclip "Upload" button + accepts drag/drop.
 *  - When a file exists: shows a green pill with the filename, plus a tiny
 *    "previous versions" link if any historical versions exist.
 *
 * All actions go through the shared ``/api/unit-documents`` endpoints so the
 * SAME file surfaces on every business that references the same unit number
 * (e.g. Harbor View LLC unit 13 ↔ Building B unit 13).
 */
import { useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Paperclip, ArrowSquareOut, Download, ClockCounterClockwise, Spinner, Trash } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024 * 1024;

function humanSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function UnitDocCell({ unit, kind, doc, canEdit, canDelete = false, onUploaded }) {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewVersion, setViewVersion] = useState(null); // version object to preview
  const [showHistory, setShowHistory] = useState(false);
  const [deletingCurrent, setDeletingCurrent] = useState(false);
  // Confirm modal state: { mode: 'current' | 'version', fileId?: string }
  const [confirmDel, setConfirmDel] = useState(null);

  // Auto-close the history modal once nothing is left to show.
  useEffect(() => {
    if (showHistory && !doc?.current_version && !(doc?.versions || []).length) {
      setShowHistory(false);
    }
  }, [doc, showHistory]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("File exceeds 5 GB limit"); return; }
    if (!unit || !canEdit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/unit-documents/${encodeURIComponent(unit)}/${kind}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded && onUploaded(data);
      toast.success(`${kind === "contract" ? "Contract" : "Closing statement"} uploaded`);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const requestDeleteCurrent = () => {
    if (!canDelete) return;
    setConfirmDel({ mode: "current" });
  };

  const requestDeleteVersion = (fileId) => {
    if (!canDelete) return;
    setConfirmDel({ mode: "version", fileId });
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    const target = confirmDel;
    setConfirmDel(null);
    if (target.mode === "current") {
      setDeletingCurrent(true);
      try {
        const { data } = await api.delete(`/unit-documents/${encodeURIComponent(unit)}/${kind}/current`);
        onUploaded && onUploaded(data.doc || null);
        toast.success("Version deleted");
      } catch (e) {
        const m = apiErrorMessage(e); if (m) toast.error(m);
      } finally {
        setDeletingCurrent(false);
      }
    } else if (target.mode === "version" && target.fileId) {
      try {
        const { data } = await api.delete(`/unit-documents/${encodeURIComponent(unit)}/${kind}/version/${target.fileId}`);
        onUploaded && onUploaded(data.doc || null);
        toast.success("Version deleted");
      } catch (e) {
        const m = apiErrorMessage(e); if (m) toast.error(m);
      }
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = ""; // reset so re-uploading same file fires onChange
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const fileUrl = (v, dl = false) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/unit-documents/file/${v.file_id}${dl ? "?download=true" : ""}`;

  const current = doc?.current_version;
  const versions = doc?.versions || [];

  // Empty-state UI: dashed dropzone + paperclip. If prior versions remain
  // (because a super admin just deleted the current file), show a small
  // "previous versions" link too so they can still browse / restore-by-replace.
  if (!current) {
    return (
      <div data-testid={`unit-doc-empty-${unit}-${kind}`}>
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`group flex items-center justify-center min-h-[34px] rounded-md border border-dashed text-[10px] sm:text-[11px] transition-colors ${
            drag ? "border-[#327de3] bg-[#327de3]/5 text-[#1f64c2]" : "border-slate-300 text-slate-400 hover:border-slate-400"
          }`}
        >
          {canEdit ? (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1 px-2 py-1.5"
              disabled={busy}
            >
              {busy ? <Spinner size={12} className="animate-spin" /> : <Paperclip size={12} />}
              {busy ? "Uploading…" : (drag ? "Drop to upload" : "Attach")}
            </button>
          ) : (
            <span className="px-2 py-1.5 text-slate-300">—</span>
          )}
          <input ref={fileRef} type="file" onChange={onPick} className="hidden" />
        </div>
        {versions.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            className="mt-0.5 text-[9px] sm:text-[10px] text-slate-400 hover:text-[#1f64c2] inline-flex items-center gap-0.5"
            data-testid={`unit-doc-history-empty-${unit}-${kind}`}
          >
            <ClockCounterClockwise size={10} /> previous versions ({versions.length})
          </button>
        )}
        <UnitDocHistoryModal
          open={showHistory}
          onClose={() => setShowHistory(false)}
          current={null}
          versions={versions}
          unit={unit}
          kind={kind}
          canDelete={canDelete}
          onPreview={(v) => { setShowHistory(false); setViewVersion(v); }}
          onDeleteVersion={requestDeleteVersion}
        />
        <UnitDocViewerModal
          version={viewVersion}
          onClose={() => setViewVersion(null)}
          downloadUrl={viewVersion ? fileUrl(viewVersion, true) : ""}
          inlineUrl={viewVersion ? fileUrl(viewVersion, false) : ""}
        />
        <ConfirmDeleteVersionDialog
          open={!!confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={confirmDelete}
        />
      </div>
    );
  }

  // Filled-state UI: green pill + view/download + previous versions link
  return (
    <div
      onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)}
      onDrop={canEdit ? onDrop : undefined}
      className={`min-w-[160px] ${drag ? "ring-2 ring-[#327de3] rounded-md" : ""}`}
      data-testid={`unit-doc-cell-${unit}-${kind}`}
    >
      <button
        type="button"
        onClick={() => setViewVersion(current)}
        className="inline-flex max-w-full items-start gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] sm:text-[11px] leading-tight font-semibold hover:bg-emerald-100 transition-colors text-left break-words whitespace-normal"
        title={current.filename}
      >
        <Paperclip size={11} weight="bold" className="shrink-0 mt-[1px]" />
        <span className="break-all">{current.filename}</span>
      </button>
      <div className="mt-1 flex items-center gap-1.5 text-[9px] sm:text-[10px] text-slate-500 flex-wrap">
        <button type="button" onClick={() => setViewVersion(current)} className="inline-flex items-center gap-0.5 hover:text-[#1f64c2]" data-testid={`unit-doc-view-${unit}-${kind}`}>
          <ArrowSquareOut size={10} /> view
        </button>
        <span className="text-slate-300">·</span>
        <a href={fileUrl(current, true)} className="inline-flex items-center gap-0.5 hover:text-[#1f64c2]" data-testid={`unit-doc-download-${unit}-${kind}`}>
          <Download size={10} /> download
        </a>
        {canEdit && (
          <>
            <span className="text-slate-300">·</span>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="hover:text-[#1f64c2]" data-testid={`unit-doc-replace-${unit}-${kind}`}>
              {busy ? "uploading…" : "replace"}
            </button>
          </>
        )}
        {canDelete && (
          <>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              onClick={requestDeleteCurrent}
              disabled={deletingCurrent}
              className="text-rose-600 hover:text-rose-700"
              data-testid={`unit-doc-delete-current-${unit}-${kind}`}
            >
              {deletingCurrent ? "deleting…" : "delete current"}
            </button>
          </>
        )}
      </div>
      {versions.length > 0 && (
        <button
          type="button"
          onClick={() => setShowHistory(true)}
          className="mt-0.5 text-[9px] sm:text-[10px] text-slate-400 hover:text-[#1f64c2] inline-flex items-center gap-0.5"
          data-testid={`unit-doc-history-${unit}-${kind}`}
        >
          <ClockCounterClockwise size={10} /> previous versions ({versions.length})
        </button>
      )}
      <input ref={fileRef} type="file" onChange={onPick} className="hidden" />

      <UnitDocViewerModal
        version={viewVersion}
        onClose={() => setViewVersion(null)}
        downloadUrl={viewVersion ? fileUrl(viewVersion, true) : ""}
        inlineUrl={viewVersion ? fileUrl(viewVersion, false) : ""}
      />
      <UnitDocHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        current={current}
        versions={versions}
        unit={unit}
        kind={kind}
        canDelete={canDelete}
        onPreview={(v) => { setShowHistory(false); setViewVersion(v); }}
        onDeleteVersion={requestDeleteVersion}
      />
      <ConfirmDeleteVersionDialog
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function UnitDocViewerModal({ version, onClose, downloadUrl, inlineUrl }) {
  if (!version) return null;
  const isPdf = (version.content_type || "").toLowerCase().includes("pdf") || (version.filename || "").toLowerCase().endsWith(".pdf");
  const isImg = (version.content_type || "").toLowerCase().startsWith("image/");
  return (
    <Dialog open={!!version} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="unit-doc-viewer-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 flex flex-row items-center justify-between">
          <div className="flex flex-col gap-0.5 min-w-0">
            <DialogTitle className="text-sm font-semibold truncate">{version.filename}</DialogTitle>
            <div className="text-[11px] text-slate-500">
              {humanSize(version.size)} · uploaded {new Date(version.uploaded_at).toLocaleString()} by {version.uploaded_by_name}
            </div>
          </div>
          <a
            href={downloadUrl}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#327de3] hover:text-[#1f64c2] mr-6"
            data-testid="unit-doc-viewer-download"
          >
            <Download size={14} /> Download
          </a>
        </DialogHeader>
        <div className="h-[75vh] bg-slate-100">
          {isPdf ? (
            <iframe title={version.filename} src={inlineUrl} className="w-full h-full border-0" />
          ) : isImg ? (
            <img src={inlineUrl} alt={version.filename} className="w-full h-full object-contain bg-white" />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-3">
              <Paperclip size={32} className="text-slate-300" />
              <div>Inline preview isn&apos;t available for this file type.</div>
              <a href={downloadUrl} className="text-[#327de3] hover:text-[#1f64c2] font-semibold">Download to view</a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnitDocHistoryModal({ open, onClose, current, versions, unit, kind, canDelete, onPreview, onDeleteVersion }) {
  if (!open) return null;
  const dl = (v) => `${process.env.REACT_APP_BACKEND_URL}/api/unit-documents/file/${v.file_id}?download=true`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="unit-doc-history-modal">
        <DialogHeader>
          <DialogTitle>
            Previous versions — Unit {unit}, {kind === "contract" ? "Contract" : "Closing statement"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {current && (
            <VersionRow v={current} label="CURRENT" highlight onPreview={onPreview} downloadUrl={dl(current)} />
          )}
          {versions.slice().reverse().map((v, idx) => (
            <VersionRow
              key={v.file_id || idx}
              v={v}
              label={`VERSION ${versions.length - idx}`}
              onPreview={onPreview}
              downloadUrl={dl(v)}
              canDelete={canDelete}
              onDelete={() => onDeleteVersion(v.file_id)}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VersionRow({ v, label, highlight, onPreview, downloadUrl, canDelete, onDelete }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`}>
      <div className="flex items-center justify-between text-[11px] font-semibold mb-1">
        <span className={highlight ? "text-emerald-700" : "text-slate-500"}>{label}</span>
        <span className="text-slate-500 font-normal">
          {new Date(v.uploaded_at).toLocaleString()}{v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ""}
        </span>
      </div>
      <div className="text-sm text-slate-800 break-all">{v.filename}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{humanSize(v.size)}</div>
      <div className="mt-2 flex items-center gap-3 text-[12px]">
        <button type="button" onClick={() => onPreview(v)} className="text-[#327de3] hover:text-[#1f64c2] inline-flex items-center gap-1 font-semibold">
          <ArrowSquareOut size={12} /> View
        </button>
        <a href={downloadUrl} className="text-[#327de3] hover:text-[#1f64c2] inline-flex items-center gap-1 font-semibold">
          <Download size={12} /> Download
        </a>
        {canDelete && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 font-semibold ml-auto"
            data-testid={`unit-doc-version-delete-${v.file_id}`}
          >
            <Trash size={12} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}


function ConfirmDeleteVersionDialog({ open, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="unit-doc-confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete this version?</DialogTitle>
          <DialogDescription className="text-sm text-slate-600 pt-1">
            Are you sure you want to delete this version of the file? This action can&apos;t be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="unit-doc-confirm-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="unit-doc-confirm-delete-confirm"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
